import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const { login, loginWithMicrosoft, msError } = useAuth();
  const [username, setUsername]   = useState('');
  const [password, setPassword]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [msLoading, setMsLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const err = await login(username, password);
    setLoading(false);
    if (err) setError(err);
    else onLogin();
  };

  const signInWithMicrosoft = async () => {
    setMsLoading(true);
    setError('');
    const err = await loginWithMicrosoft();
    setMsLoading(false);
    if (err) setError(err);
    else onLogin();
  };

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', height: '100%' }}>
      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '380px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <h1 style={{ fontFamily: '"Fraunces Variable", "Fraunces", Georgia, serif', fontSize: '32px', fontWeight: 400, letterSpacing: '-0.01em', color: '#111', margin: 0, lineHeight: 1.1 }}>Goes West</h1>
          <p style={{ fontSize: '13px', color: '#737373', marginTop: '10px' }}>Sign in to continue.</p>
        </div>

        {(msError) && (
          <div style={{ fontSize: '12px', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px' }}>
            {msError}
          </div>
        )}

        {/* Microsoft SSO button */}
        <button
          onClick={signInWithMicrosoft}
          disabled={msLoading || loading}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#fff', color: '#111', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: 600, cursor: msLoading ? 'default' : 'pointer', opacity: msLoading ? 0.6 : 1, marginBottom: '20px' }}
        >
          <MicrosoftIcon />
          {msLoading ? 'Signing in…' : 'Sign in with Microsoft'}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ flex: 1, height: '1px', background: '#e5e5e5' }} />
          <span style={{ fontSize: '11px', color: '#a3a3a3', fontWeight: 500 }}>or use username</span>
          <div style={{ flex: 1, height: '1px', background: '#e5e5e5' }} />
        </div>

        <form onSubmit={submit} method="post" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label htmlFor="login-username" style={{ fontSize: '12px', fontWeight: 600, color: '#404040', display: 'block', marginBottom: '5px' }}>Username</label>
            <input
              id="login-username"
              name="username"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '9px 12px', fontSize: '13px', color: '#171717', background: 'var(--bg)', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
              placeholder="your.username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
            />
          </div>
          <div>
            <label htmlFor="login-password" style={{ fontSize: '12px', fontWeight: 600, color: '#404040', display: 'block', marginBottom: '5px' }}>Password</label>
            <input
              id="login-password"
              name="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              style={{ border: '1px solid #e5e5e5', borderRadius: '8px', padding: '9px 12px', fontSize: '13px', color: '#171717', background: 'var(--bg)', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
              placeholder="••••••••"
            />
          </div>
          {error && <p style={{ fontSize: '12px', color: '#dc2626', margin: 0 }}>{error}</p>}
          <button
            type="submit"
            disabled={loading || msLoading}
            style={{ background: 'var(--crimson)', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, refresh } = useAuth();

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ width: '20px', height: '20px', border: '2px solid #e5e5e5', borderTopColor: '#111', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  if (!user) return <LoginScreen onLogin={refresh} />;

  return <>{children}</>;
}
